<script setup>
import { useModal } from 'vue-final-modal';
import BiDatasetEditorPopup from '~/bi/components/dataset-picker/dataset-picker-upload/bi-dataset-editor-popup.vue';
import { useBiDataFormatter } from '~/bi/components/widgets/table-widget/composables/bi-table-data-formatter.composable.js';
import HawkLoader from '~/common/components/atoms/hawk-loader.vue';
import { useCommonImports } from '~/common/composables/common-imports.composable';
import { useCsvParser } from '~/common/composables/csv-parser.composable.js';
import { getColumnNameValidator } from '~/common/composables/custom-validators.js';
import { sleep } from '~/common/utils/common.utils';

const emit = defineEmits(['published']);
const { parseFile } = useCsvParser();
const { $t, $toast } = useCommonImports();
const { buildTableColumns } = useBiDataFormatter();
const form = ref({
  name: null,
  file: null,
});
const data = ref(null);
const is_parsing = ref(false);
const columns = ref([]);
async function createData(file) {
  try {
    if (!file) {
      columns.value = [];
      data.value = null;
      return;
    }
    is_parsing.value = true;
    let parsed_data = await parseFile(file);
    parsed_data = parsed_data.map(row => row.map(cell => String(cell ?? '').replace(/[\n\t]/g, '')));
    columns.value = parsed_data.splice(0, 1)[0].filter(column => column.trim() !== '');
    validateColumns(columns.value);
    // .map(column => ({
    //   accessorKey: column,
    //   header: column,
    //   id: column,
    // }));
    data.value = parsed_data.map((row) => {
      return row.reduce((obj, value, i) => {
        // obj[columns.value[i].accessorKey] = value;
        obj[columns.value[i]] = value;
        return obj;
      }, {});
    });
  }
  catch (err) {
    logger.error(err);
    $toast({
      title: 'Error',
      text: err.message,
      type: 'error',
    });
  }
  finally {
    is_parsing.value = false;
  }
}

function validateColumns(columns) {
  const seen = new Set();

  for (const column of columns) {
    if (seen.has(column)) {
      throw new Error(`Duplicates found for the column: ${column}`);
    }
    if (column.length > 48) {
      throw new Error(
        `Invalid column name "${column}". Column name must not exceed 48 characters.`,
      );
    }
    seen.add(column.title);
  }
}

async function handleOpenEditTable() {
  const table_columns = await buildTableColumns(data.value, columns.value);

  const bi_dataset_editor_popup = useModal({
    component: BiDatasetEditorPopup,
    attrs: {
      name: form.value.name,
      columns: table_columns,
      data: data.value,
      onClose() {
        bi_dataset_editor_popup.close();
      },
      onPublished() {
        emit('published');
        bi_dataset_editor_popup.close();
      },
    },
  });
  bi_dataset_editor_popup.open();
}

async function save() {
  try {
    if (!columns.value?.length && !data.value?.length) {
      throw new Error('The uploaded file appears to be empty. Please check the file and try again.');
    }
    if (!data.value?.length) {
      throw new Error('No data found in the uploaded file. Please ensure the file contains data rows.');
    }
    await sleep(1000);
    await handleOpenEditTable();
  }
  catch (err) {
    logger.error(err);
    $toast({
      title: 'Error',
      text: err.message,
      type: 'error',
    });
  }
  finally {
    await sleep(1000);
  }
}
</script>

<template>
  <div>
    <Vueform v-model="form" size="sm" class="max-w-[800px]" :endpoint="save" :display-errors="false" :columns="{ sm: { container: 12, label: 4, wrapper: 12 } }">
      <div class="col-span-12 grid grid-cols-12 gap-4 pl-4 pt-4">
        <TextElement
          name="name" label="Dataset name"
          description="Enter the name using slashes ('/'') to define hierarchy."
          class="w-full"
          :rules="['required', getColumnNameValidator()]"
        />
        <FileElement
          name="file"
          class="mb-5"
          :label="$t('Choose file')"
          :presets="['hawk_file_element']"
          :options="{
            clickable_text: $t('Click to upload'),
            text: 'or drag and drop',
            description: '.csv, .xlsx',
          }"
          rules="required"
          accept=".xlsx,.csv"
          :auto="false"
          :drop="true"
          :use_uppy="false"
          @change="createData"
        />
        <!-- <div v-if="is_parsing" class="col-span-12 flex justify-center">
          <HawkLoader />
        </div> -->
        <!-- <div v-else-if="data" class="col-span-12 flex justify-center -ml-8">
          <HawkTable :data="data" :columns="columns" />
        </div> -->
        <div class="col-span-12 flex justify-end sticky bottom-0 bg-white ">
          <ButtonElement :submits="true" name="submit" :disabled="!data?.length">
            {{ $t('Next') }}
          </ButtonElement>
        </div>
      </div>
    </Vueform>
  </div>
</template>
